/** The live state catalog — the same states.json + per-state manifest.json
 * the iOS app streams from. tiles.dustlark.app is R2 behind Cloudflare:
 * $0 egress, range requests, no server of ours in the path. */

export const TILES = "https://tiles.dustlark.app";

export interface StateEntry {
  region: string;
  name: string;
  state: string;
  bbox: [number, number, number, number]; // w, s, e, n
  layers: string[];
  total_bytes: number;
  built: string;
}

export interface ManifestLayer {
  file: string;
  bytes: number;
  sha: string;
  encoding?: string;
  /** The routing layer's DLR2 writer version (2 = way/string tables the
   * browser router needs) — merged from routing.meta.json by package.py. */
  graphVersion?: number;
}

export interface StateManifest {
  region: string;
  name: string;
  bbox: number[];
  layers: Record<string, ManifestLayer>;
  /** Spatial index sidecar for browser snap-routing (DLSG v1), published
   * as a TOP-LEVEL key rather than a layer so the phone's downloader —
   * which walks `layers` — never fetches it. */
  snapgrid?: ManifestLayer;
  built?: string;
}

let statesCache: StateEntry[] | null = null;
const manifestCache = new Map<string, StateManifest>();

// R2 serves these JSONs with no cache-control, so browsers fall back to
// heuristic freshness keyed off Last-Modified — days of staleness for a
// returning visitor right after a data publish (seen live: a manifest
// published minutes earlier still showed the old built date). "no-cache"
// forces a conditional revalidation instead: an ETag match costs a 304,
// a real update arrives immediately.
export async function fetchStates(): Promise<StateEntry[]> {
  if (statesCache) return statesCache;
  const r = await fetch(`${TILES}/states.json`, { cache: "no-cache" });
  if (!r.ok) throw new Error(`states.json ${r.status}`);
  const j = await r.json();
  statesCache = j.states as StateEntry[];
  return statesCache;
}

export async function fetchManifest(region: string): Promise<StateManifest> {
  const hit = manifestCache.get(region);
  if (hit) return hit;
  const r = await fetch(`${TILES}/${region}/manifest.json`, { cache: "no-cache" });
  if (!r.ok) throw new Error(`${region}/manifest.json ${r.status}`);
  const m = (await r.json()) as StateManifest;
  manifestCache.set(region, m);
  return m;
}

/** pmtiles URL for a layer this state actually has, else null — mirrors
 * RegionStore.layerURL's "listed AND present" discipline. */
export function layerURL(m: StateManifest, name: string): string | null {
  const layer = m.layers[name];
  if (!layer) return null;
  return `${TILES}/${m.region}/${layer.file}`;
}

/** Which state's bbox contains the point; ties go to the smaller bbox
 * (a corridor state beats a mega-state overlap). Pure — tested. */
export function stateContaining(
  states: StateEntry[],
  lon: number,
  lat: number,
): StateEntry | null {
  const hits = states.filter(
    (s) =>
      lon >= s.bbox[0] && lon <= s.bbox[2] && lat >= s.bbox[1] && lat <= s.bbox[3],
  );
  if (hits.length === 0) return null;
  hits.sort(
    (a, b) =>
      (a.bbox[2] - a.bbox[0]) * (a.bbox[3] - a.bbox[1]) -
      (b.bbox[2] - b.bbox[0]) * (b.bbox[3] - b.bbox[1]),
  );
  return hits[0];
}

export function bboxCenter(s: StateEntry): [number, number] {
  return [(s.bbox[0] + s.bbox[2]) / 2, (s.bbox[1] + s.bbox[3]) / 2];
}

/** #state=colorado&look=ustopo style hash — shareable, survives reload. */
export function parseHash(hash: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of hash.replace(/^#/, "").split("&")) {
    const [k, v] = part.split("=");
    if (k && v) out[k] = decodeURIComponent(v);
  }
  return out;
}

export function buildHash(params: Record<string, string>): string {
  return (
    "#" +
    Object.entries(params)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&")
  );
}
